function* concat(source, ...others) {
    yield* source;
    for (const other of others) {
        yield* other;
    }
}

function* distinct(iterable) {
    const unique = new Set();
    for (const item of iterable) {
        if (!unique.has(item)) {
            unique.add(item);
            yield item;
        }
    }
}

function* filter(iterable, predicate) {
    for (const item of iterable) {
        if (predicate(item)) {
            yield item;
        }
    }
}

function* flatMap(iterable, mapperFn) {
    for (const item of iterable) {
        const sequence = mapperFn(item);
        yield* sequence;
    }
}

function* groupBy(iterable, keySelector, elementSelector) {
    const groups = new Map();
    for (const item of iterable) {
        let key = keySelector(item);
        // Check if we need to hash the key so that equality works correctly
        // with Map. This is to support object's (and array's) as the key.
        // We don't want to hash every single item, because it slows down
        // the algorithm a lot.
        const needToHash = typeof key === "object";
        if (needToHash) {
            key = JSON.stringify(key);
        }
        const value = elementSelector
            ? elementSelector(item)
            : item;
        let group = groups.get(key);
        if (!group) {
            group = { isKeyHashed: needToHash, items: [] };
            groups.set(key, group);
        }
        group.items.push(value);
    }
    for (const keyItemsPair of groups.entries()) {
        yield {
            key: keyItemsPair[1].isKeyHashed
                ? JSON.parse(keyItemsPair[0])
                : keyItemsPair[0],
            items: keyItemsPair[1].items,
        };
    }
}

function* map(source, mapFn) {
    for (const item of source) {
        yield mapFn(item);
    }
}

function* prepend(source, ...others) {
    for (const other of others) {
        yield* other;
    }
    yield* source;
}

/**
 * Copies items from the given iterable into an array
 */
function copyIntoAnArray(iterable) {
    // Optimization for arrays, as .slice() by far fastest way to
    // create a shallow copy https://jsbench.me/pmk2aqrlo1
    if (Array.isArray(iterable)) {
        return iterable.slice();
    }
    // With iterables for-of loop is the fastest https://jsbench.me/pwk2ar61ei
    const result = [];
    for (const item of iterable) {
        result.push(item);
    }
    return result;
}
/**
 * Creates an iterable from given generator function
 *
 * @param generatorFn
 * @param args
 */
const iterableFromGenerator = (generatorFn, args) => ({
    [Symbol.iterator]: () => generatorFn.apply(undefined, args),
});

function* reverse(iterable) {
    const items = copyIntoAnArray(iterable);
    for (let i = items.length - 1; i >= 0; i--) {
        yield items[i];
    }
}

function* skip(iterable, howMany) {
    let numSkipped = 0;
    for (const item of iterable) {
        if (numSkipped < howMany) {
            numSkipped++;
            continue;
        }
        yield item;
    }
}

function* skipWhile(iterable, predicate) {
    let startTaking = false;
    for (const item of iterable) {
        if (startTaking) {
            yield item;
        }
        else if (!predicate(item)) {
            yield item;
            startTaking = true;
        }
    }
}

function* sortBy(iterable, comparer) {
    yield* copyIntoAnArray(iterable).sort(comparer);
}

function* take(iterable, howMany) {
    if (howMany < 1) {
        return;
    }
    let numTaken = 0;
    for (const item of iterable) {
        numTaken++;
        yield item;
        if (numTaken >= howMany) {
            break;
        }
    }
}

function* takeWhile(iterable, predicate) {
    for (const item of iterable) {
        if (!predicate(item)) {
            break;
        }
        yield item;
    }
}

function* without(iterator, withoutItems, comparePredicate) {
    // cache already found results
    const cache = new Set();
    outer: for (const item of iterator) {
        // fast path, this item was already found, don't loop
        if (cache.has(item))
            continue;
        // slow path, loop over each item in the set, determine if it matches the ComparePredicate
        for (const withoutItem of withoutItems) {
            // if the item is found, add it to the cache and skip the item
            if (comparePredicate(item, withoutItem)) {
                cache.add(item);
                continue outer;
            }
        }
        // we can safely yield the item
        yield item;
    }
}

const identityPredicateFn = (x) => x;
const defaultComparer = (a, b) => {
    if (a === b) {
        return 0;
    }
    if (a < b) {
        return -1;
    }
    return 1;
};
/**
 * A sequence of items
 */
class SequenceImpl {
    constructor(_iterable) {
        this._iterable = _iterable;
        this[Symbol.iterator] = () => this._iterable[Symbol.iterator]();
    }
    concat(...others) {
        return this._sequenceFromGenerator(concat, others);
    }
    distinct() {
        return this._sequenceFromGenerator(distinct);
    }
    every(predicate = identityPredicateFn) {
        for (const item of this._iterable) {
            if (!predicate(item)) {
                return false;
            }
        }
        return true;
    }
    filter(predicate) {
        return this._sequenceFromGenerator(filter, [predicate]);
    }
    find(predicate) {
        for (const item of this._iterable) {
            if (predicate(item)) {
                return item;
            }
        }
        return undefined;
    }
    first() {
        for (const item of this._iterable) {
            return item;
        }
        return undefined;
    }
    flatMap(mapperFn) {
        return this._sequenceFromGenerator(flatMap, [mapperFn]);
    }
    forEach(callback) {
        for (const item of this._iterable) {
            callback(item);
        }
    }
    groupBy(keySelector, elementSelector) {
        if (typeof keySelector === "string") {
            keySelector = createSelectByKey(keySelector);
        }
        return this._sequenceFromGenerator(groupBy, [
            keySelector,
            elementSelector,
        ]);
    }
    includes(searchItem) {
        for (const item of this._iterable) {
            if (item === searchItem) {
                return true;
            }
        }
        return false;
    }
    isEmpty() {
        return !this.some(() => true);
    }
    last() {
        const items = this.toArray();
        return items.length === 0 ? undefined : items[items.length - 1];
    }
    max() {
        let result = undefined;
        for (const item of this._iterable) {
            if (result === undefined) {
                result = item;
            }
            else if (item > result) {
                result = item;
            }
        }
        return result;
    }
    map(mapFn) {
        return this._sequenceFromGenerator(map, [mapFn]);
    }
    min() {
        let result = undefined;
        for (const item of this._iterable) {
            if (result === undefined) {
                result = item;
            }
            else if (item < result) {
                result = item;
            }
        }
        return result;
    }
    pick(...keys) {
        return this.map((item) => {
            const result = {};
            for (const key of keys) {
                if (key in item) {
                    result[key] = item[key];
                }
            }
            return result;
        });
    }
    prepend(...items) {
        return this._sequenceFromGenerator(prepend, items);
    }
    reduce(callback, accumulator) {
        for (const item of this._iterable) {
            accumulator = callback(accumulator, item);
        }
        return accumulator;
    }
    reverse() {
        return this._sequenceFromGenerator(reverse);
    }
    skip(howMany) {
        return this._sequenceFromGenerator(skip, [howMany]);
    }
    skipWhile(predicate) {
        return this._sequenceFromGenerator(skipWhile, [predicate]);
    }
    some(predicate = identityPredicateFn) {
        for (const item of this._iterable) {
            if (predicate(item)) {
                return true;
            }
        }
        return false;
    }
    sortBy(keySelector, comparer) {
        const compareFn = createCompareFn(false, keySelector, comparer);
        return new OrderedSequenceImpl(this._iterable, compareFn);
    }
    sortByDescending(keySelector, comparer) {
        const compareFn = createCompareFn(true, keySelector, comparer);
        return new OrderedSequenceImpl(this._iterable, compareFn);
    }
    sum(valueSelector) {
        let result = undefined;
        for (const item of this._iterable) {
            let itemToSum = valueSelector ? valueSelector(item) : item;
            if (result === undefined) {
                result = itemToSum;
            }
            else {
                result +=
                    typeof itemToSum === "number"
                        ? itemToSum
                        : String(itemToSum);
            }
        }
        return result === undefined ? 0 : result;
    }
    take(howMany) {
        return this._sequenceFromGenerator(take, [howMany]);
    }
    takeWhile(predicate) {
        return this._sequenceFromGenerator(takeWhile, [predicate]);
    }
    without(items, predicate) {
        if (!predicate) {
            const withoutSet = new Set(items);
            return this.filter((item) => !withoutSet.has(item));
        }
        else {
            return this._sequenceFromGenerator(without, [items, predicate]);
        }
    }
    toArray() {
        return copyIntoAnArray(this._iterable);
    }
    toMap(keySelectorFn, elementSelectorFn) {
        const map = new Map();
        for (const item of this._iterable) {
            const key = keySelectorFn(item);
            const value = elementSelectorFn ? elementSelectorFn(item) : item;
            map.set(key, value);
        }
        return map;
    }
    toObject(keySelectorFn, elementSelectorFn) {
        const object = {};
        for (const item of this._iterable) {
            const key = keySelectorFn(item);
            const value = elementSelectorFn ? elementSelectorFn(item) : item;
            object[key] = value;
        }
        return object;
    }
    toSet() {
        return new Set(this._iterable);
    }
    toString(separator = ",") {
        return Array.isArray(this._iterable)
            ? this._iterable.join(separator)
            : copyIntoAnArray(this._iterable).join(separator);
    }
    _sequenceFromGenerator(factoryFn, restArgs) {
        const iterableArg = [this._iterable];
        return new SequenceImpl(iterableFromGenerator(factoryFn, restArgs ? iterableArg.concat(restArgs) : iterableArg));
    }
}
/**
 * Ordered sequence of elements
 */
class OrderedSequenceImpl extends SequenceImpl {
    constructor(_iterableToSort, comparer) {
        super(iterableFromGenerator(sortBy, [_iterableToSort, comparer]));
        this._iterableToSort = _iterableToSort;
        this._comparer = comparer;
    }
    thenBy(keySelector, comparer) {
        const thenComparer = createCompareFn(false, keySelector, comparer);
        const compareFn = createChainedCompareFn(this._comparer, thenComparer);
        return new OrderedSequenceImpl(this._iterableToSort, compareFn);
    }
    thenByDescending(keySelector, comparer) {
        const thenComparer = createCompareFn(true, keySelector, comparer);
        const compareFn = createChainedCompareFn(this._comparer, thenComparer);
        return new OrderedSequenceImpl(this._iterableToSort, compareFn);
    }
}
/**
 * Creates a compare function that sorts TItems either ascending or descending
 * using the given key selector and comparer.
 *
 * @param descending  Sort descending or ascending
 * @param keySelector Optional function to select the key that is used for sorting
 * @param comparer    Optional comparer function to compare the keys
 */
const createCompareFn = (descending, keySelector, comparer) => {
    const compareFn = comparer || defaultComparer;
    return keySelector
        ? (a, b) => descending
            ? compareFn(keySelector(b), keySelector(a))
            : compareFn(keySelector(a), keySelector(b))
        : (a, b) => descending ? defaultComparer(b, a) : defaultComparer(a, b);
};
/**
 * Chains two compare functions. First sort by firstCompare. If items
 * are equal, then sorts by secondCompare.
 *
 * @param firstCompare
 * @param secondCompare
 */
const createChainedCompareFn = (firstCompare, secondCompare) => (a, b) => {
    const firstResult = firstCompare(a, b);
    return firstResult === 0 ? secondCompare(a, b) : firstResult;
};
const createSelectByKey = (key) => (item) => key in item ? item[key] : undefined;

function createObjectIterable(object) {
    return iterableFromGenerator(objectIterator, [object]);
}
function* objectIterator(object) {
    for (const key in object) {
        if (object.hasOwnProperty(key)) {
            const value = object[key];
            yield [key, value];
        }
    }
}

/**
 * Entrypoint of the library
 */
function _isIterable(object) {
    return typeof object[Symbol.iterator] === "function";
}
function from(iterable) {
    if (iterable instanceof SequenceImpl) {
        return iterable;
    }
    else if (_isIterable(iterable)) {
        return new SequenceImpl(iterable);
    }
    else {
        return new SequenceImpl(createObjectIterable(iterable));
    }
}

export { from };
//# sourceMappingURL=fromfrom.es5.js.map
